import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { readTableState, writeTableState } from './tableState';

const PER_PAGE = 10;

const isFilled = (value) => value !== '' && value !== null && value !== undefined;

/**
 * Estado de listagem paginada com busca e filtros, persistido em localStorage por `storageKey`.
 * `fetcher(params)` deve retornar a resposta do axios (paginada ou array).
 */
export default function useTableList({ storageKey, fetcher, errorMessage = 'Não foi possível carregar os registros' }) {
    const { toastApiError } = useToast();
    const [initial] = useState(() => readTableState(storageKey));

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [pagination, setPagination] = useState({ currentPage: 1, perPage: PER_PAGE, totalItems: 0 });
    const [searchValue, setSearchValue] = useState(initial.search);
    const [filterValues, setFilterValues] = useState(initial.filters);

    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;
    const applied = useRef({ search: initial.search, filters: initial.filters });
    const pageRef = useRef(initial.page);
    const requestId = useRef(0);

    const load = useCallback(async (page = 1, search = applied.current.search, filters = applied.current.filters) => {
        applied.current = { search, filters };
        pageRef.current = page;
        setSearchValue(search);
        setFilterValues(filters);
        writeTableState(storageKey, { search, filters, page });

        const currentRequest = ++requestId.current;
        setLoading(true);
        try {
            const params = { page, per_page: PER_PAGE, search };
            Object.entries(filters).forEach(([key, value]) => {
                if (isFilled(value)) params[key] = value;
            });

            const response = await fetcherRef.current(params);
            if (currentRequest !== requestId.current) return;
            const body = response.data;

            if (body?.data) {
                const total = body.total || 0;
                const lastPage = Math.max(1, Math.ceil(total / (body.per_page || PER_PAGE)));
                if (page > lastPage) {
                    await load(lastPage, search, filters);
                    return;
                }
                setRows(body.data);
                setPagination({
                    currentPage: body.current_page || 1,
                    perPage: body.per_page || PER_PAGE,
                    totalItems: total,
                });
            } else {
                const list = Array.isArray(body) ? body : [];
                setRows(list);
                setPagination({ currentPage: 1, perPage: PER_PAGE, totalItems: list.length });
            }
        } catch (e) {
            if (currentRequest !== requestId.current) return;
            console.error('Error loading list:', e);
            toastApiError(e, { title: 'Erro ao carregar', fallback: errorMessage });
        } finally {
            if (currentRequest === requestId.current) {
                setLoading(false);
                setLoaded(true);
            }
        }
    }, [storageKey, errorMessage, toastApiError]);

    useEffect(() => {
        load(initial.page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reload = useCallback(() => load(pageRef.current), [load]);

    return { rows, loading, loaded, pagination, searchValue, filterValues, load, reload };
}
